// src/services/api/client.ts

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getIdToken, signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';

// Fallback a la misma URL del frontend cuando no se define VITE_SGC_API_URL (p.ej. en App Hosting).
const apiBase =
  (import.meta.env.VITE_SGC_API_URL?.trim() ||
    import.meta.env.VITE_API_URL?.trim() ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001'))
      .replace(/\/+$/, ''); // evita doble slash

const apiVersion = (import.meta.env.VITE_SGC_API_VERSION || import.meta.env.VITE_API_VERSION || 'v1')
  .replace(/^\/+|\/+$/g, '');

export const apiClient = axios.create({
  baseURL: `${apiBase}/api/${apiVersion}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - agregar token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const user = auth.currentUser;
    if (user) {
      const token = await getIdToken(user);
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - manejar errores
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const hasFirebaseUser = !!auth.currentUser;
      // Evitar bucle de logout si el backend responde 401 pero la sesión de Firebase sigue válida.
      if (!hasFirebaseUser) {
        try {
          await signOut(auth);
        } catch {
          // noop
        }
        window.location.href = '/login';
      } else {
        // Mantener la sesión; el caller manejará el error (p.ej. mostrar mensaje).
        console.warn('[api] 401 recibido con sesión Firebase activa:', error.config?.url);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
